import { clockMinutes, endForStorage, moveItem } from '../layout/media'
import { DEFAULT_PICTURE_SECONDS } from '../layout/playlist'
import type { BranchSeat, Campaign, CampaignBody, PlaylistEntry, SignageMedia, SignagePlace, TickerLine } from './api'
import type { StaffKey, StaffLanguage } from './staffText'
import { KIOSK_SIGNAGE } from './signage'
import { formatMoment, t } from './staffText'

// The Signage page's rules, mirrored from internal/signage; the server is the authority.

/** What the library's and a design's upload pickers offer; a .mov is taken when it is H.264 (internal/signage). */
export const LIBRARY_ACCEPT = 'image/png,image/jpeg,video/mp4,video/webm,video/quicktime,.png,.jpg,.jpeg,.mp4,.webm,.mov'
export const DESIGN_MEDIA_ACCEPT = 'image/png,image/jpeg,image/gif,video/mp4,video/webm,video/quicktime,.png,.jpg,.jpeg,.gif,.mp4,.webm,.mov'

export const MAX_ENTRIES = 50
export const MAX_TICKER_LINES = 10
export const MIN_SECONDS = 3
export const MAX_SECONDS = 3600
/** The places a campaign may name: the hall TV, and the kiosk's two when kiosk signage is on. */
export function placesFor(kioskSignage: boolean): readonly SignagePlace[] {
  return kioskSignage ? ['board', 'kioskIdle', 'kioskBanner'] : ['board']
}
export const PLACES: readonly SignagePlace[] = placesFor(KIOSK_SIGNAGE)

export const PLACE_KEYS: Record<SignagePlace, StaffKey> = {
  board: 'signage.place.board',
  kioskIdle: 'signage.place.kioskIdle',
  kioskBanner: 'signage.place.kioskBanner',
}

export const STATE_KEYS: Record<NonNullable<Campaign['state']>, StaffKey> = {
  off: 'signage.state.off',
  scheduled: 'signage.state.scheduled',
  running: 'signage.state.running',
  ended: 'signage.state.ended',
  outside: 'signage.state.outside',
  outranked: 'signage.state.outranked',
  noSeat: 'signage.state.noSeat',
  empty: 'signage.state.empty',
}

export type SignageTab = 'library' | 'playlists' | 'campaigns' | 'screens'

/** The tab an address's hash names (#playlists/<id> opens one), the library otherwise. */
export function tabOf(hash: string): SignageTab {
  const t = hash.replace(/^#/, '').split('/')[0]
  return t === 'playlists' || t === 'campaigns' || t === 'screens' ? t : 'library'
}

/** The playlist an address opens, #playlists/<id>; '' for none. */
export function playlistOf(hash: string): string {
  const [tab, id = ''] = hash.replace(/^#/, '').split('/')
  return tab === 'playlists' ? decodeURIComponent(id) : ''
}

/** A new item for a library file: a picture for ten seconds, a film to its end. */
export function entryFor(m: SignageMedia): PlaylistEntry {
  return { mediaItemId: m.id, seconds: m.kind === 'video' ? 0 : DEFAULT_PICTURE_SECONDS, fit: 'fit' }
}

/** A playlist's running time in seconds; a film playing to its end counts its own length, a picture with none ten. */
export function totalSeconds(entries: readonly PlaylistEntry[], library: readonly SignageMedia[]): number {
  return entries.reduce((n, e) => {
    if (e.seconds > 0) return n + e.seconds
    const m = library.find((x) => x.id === e.mediaItemId)
    if (m?.kind === 'image') return n + DEFAULT_PICTURE_SECONDS
    return n + Math.round((m?.durationMs ?? 0) / 1000)
  }, 0)
}

/** An item dragged, or moved by a button, from one place to another. */
export const moveEntry = moveItem

/** A running time as m:ss, or h:mm:ss from an hour. */
export function clockOf(seconds: number): string {
  const ss = String(seconds % 60).padStart(2, '0')
  if (seconds < 3600) return `${Math.floor(seconds / 60)}:${ss}`
  return `${Math.floor(seconds / 3600)}:${String(Math.floor((seconds % 3600) / 60)).padStart(2, '0')}:${ss}`
}

/** Bytes as megabytes, one decimal (a comma in az and ru); a small file reads 0.1, not empty. */
export function megabytes(bytes: number, lang: StaffLanguage = 'en'): string {
  const mb = bytes > 0 ? Math.max(bytes / (1 << 20), 0.1) : 0
  const s = mb.toFixed(1)
  return lang === 'en' ? s : s.replace('.', ',')
}

/** What the library's preview shows: a film played itself, its still frame as the poster; a picture whole. */
export function mediaPreview(
  m: Pick<SignageMedia, 'kind' | 'assetId'> & { posterAssetId?: string },
  url: (assetId: string) => string,
): { kind: 'video' | 'image'; src: string; poster?: string } {
  if (m.kind !== 'video') return { kind: 'image', src: url(m.assetId) }
  return m.posterAssetId ? { kind: 'video', src: url(m.assetId), poster: url(m.posterAssetId) } : { kind: 'video', src: url(m.assetId) }
}

/** A film's length as m:ss, empty for a picture or a film not measured. */
export function filmLength(m: Pick<SignageMedia, 'kind' | 'durationMs'>): string {
  const s = Math.round((m.durationMs ?? 0) / 1000)
  return m.kind === 'video' && s > 0 ? clockOf(s) : ''
}

/** A ticker without the lines blank in every language; the server 422s one (internal/signage/signage.go). */
export function nonBlankTicker(ticker: readonly TickerLine[]): TickerLine[] {
  return ticker.filter((l) => Object.values(l).some((v) => v.trim()))
}

/** What the server would refuse about a playlist, as the page's phrase keys. */
export function playlistProblems(
  name: string,
  entries: readonly PlaylistEntry[],
  ticker: readonly TickerLine[],
  library: readonly SignageMedia[],
): StaffKey[] {
  const out: StaffKey[] = []
  if (!name.trim()) out.push('signage.problem.name')
  if (entries.length > MAX_ENTRIES) out.push('signage.problem.tooMany')
  const kindOf = new Map(library.map((m) => [m.id, m.kind]))
  const wrong = entries.some(
    (e) => !(e.seconds === 0 && kindOf.get(e.mediaItemId) === 'video') && (e.seconds < MIN_SECONDS || e.seconds > MAX_SECONDS),
  )
  if (wrong) out.push('signage.problem.seconds')
  if (nonBlankTicker(ticker).length > MAX_TICKER_LINES) out.push('signage.problem.ticker')
  return out
}

/** A campaign as its form edits it: hours as HH:MM, all day as a switch. */
export interface CampaignForm {
  name: string
  playlistId: string
  startDate: string
  endDate: string
  weekdays: number[]
  allDay: boolean
  from: string
  to: string
  priority: 'normal' | 'high'
  active: boolean
  branchIds: string[]
  places: SignagePlace[]
}

const hhmm = (min: number) => `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`

/** The form for a stored campaign, or a new one that starts today, all day, on the hall TV. */
export function formOf(c: Campaign | undefined, today: string): CampaignForm {
  if (!c) {
    return {
      name: '', playlistId: '', startDate: today, endDate: today, weekdays: [], allDay: true,
      from: '09:00', to: '18:00', priority: 'normal', active: true, branchIds: [], places: ['board'],
    }
  }
  const allDay = c.fromMin === 0 && (c.toMin === 0 || c.toMin === 1440)
  return {
    name: c.name, playlistId: c.playlistId, startDate: c.startDate, endDate: c.endDate, weekdays: [...c.weekdays],
    allDay, from: allDay ? '09:00' : hhmm(c.fromMin), to: allDay ? '18:00' : hhmm(c.toMin % 1440),
    priority: c.priority, active: c.active, branchIds: [...c.branchIds], places: c.places.filter((p) => PLACES.includes(p)),
  }
}

/** What the server is sent: hours in minutes, an end of 00:00 as midnight, all day as 0 and 0. */
export function bodyOf(f: CampaignForm): CampaignBody {
  return {
    name: f.name.trim(),
    playlistId: f.playlistId,
    startDate: f.startDate,
    endDate: f.endDate,
    weekdays: [...f.weekdays].sort((a, b) => a - b),
    fromMin: f.allDay ? 0 : (clockMinutes(f.from) ?? 0),
    toMin: f.allDay ? 0 : (clockMinutes(endForStorage(f.to)) ?? 0),
    priority: f.priority,
    active: f.active,
    branchIds: f.branchIds,
    places: f.places,
  }
}

/** What is missing from a campaign, as the page's phrase keys. today is the organisation's day;
 *  storedEnd is the saved campaign's end, which may stay in the past (renaming an ended one). */
export function campaignProblems(f: CampaignForm, today = '', storedEnd = ''): StaffKey[] {
  const out: StaffKey[] = []
  if (!f.name.trim()) out.push('signage.problem.name')
  if (!f.playlistId) out.push('signage.problem.playlist')
  if (!f.startDate || !f.endDate || f.endDate < f.startDate) out.push('signage.problem.dates')
  else if (today && f.endDate < today && f.endDate !== storedEnd) out.push('signage.problem.past')
  if (!f.allDay) {
    const b = bodyOf(f)
    if (b.fromMin >= b.toMin) out.push('signage.problem.hours')
  }
  if (f.branchIds.length === 0) out.push('signage.problem.branches')
  if (f.places.length === 0) out.push('signage.problem.places')
  return out
}

/** A list with a value ticked on or off, in the order it was ticked. */
export function toggled<T>(list: readonly T[], value: T, on: boolean): T[] {
  if (!on) return list.filter((v) => v !== value)
  return list.includes(value) ? [...list] : [...list, value]
}

/** The branch "Plays now" opens on: the first holding a Signage seat, else the first. */
export function nowBranch(branches: readonly BranchSeat[]): string {
  return (branches.find((b) => b.seat) ?? branches[0])?.id ?? ''
}

/** The campaign's branches by name, as the list shows them, each with its note; a branch no longer listed is left out. */
export function campaignBranches(c: Pick<Campaign, 'branchIds'>, branches: readonly BranchSeat[], note?: (b: BranchSeat) => string): string {
  return branches
    .filter((b) => c.branchIds.includes(b.id))
    .map((b) => {
      const n = note?.(b)
      return n ? `${b.name} · ${n}` : b.name
    })
    .join(', ')
}

/** Why a campaign cannot show at a branch: no Signage seat, else no screen there with a playlist area. */
export function branchNote(b: BranchSeat): StaffKey | undefined {
  if (!b.seat) return 'signage.state.noSeat'
  if (!b.playlistArea) return 'signage.noPlaylistArea'
  return undefined
}

/** The campaign's days as phrase keys; none is every day. */
export function campaignDayKeys(c: Pick<Campaign, 'weekdays'>): StaffKey[] {
  return [...c.weekdays].sort((a, b) => a - b).map((d) => `designs.media.day.${d}` as StaffKey)
}

/** The campaign's hours as HH:MM–HH:MM; empty for all day. */
export function campaignHours(c: Pick<Campaign, 'fromMin' | 'toMin'>): string {
  return c.fromMin === 0 && c.toMin === 0 ? '' : `${hhmm(c.fromMin)}–${hhmm(c.toMin)}`
}

/** Changes whenever a campaign is saved, switched off or removed: "Plays now" asks again then. */
export function campaignsStamp(campaigns: readonly Campaign[]): string {
  return campaigns.map((c) => `${c.id}:${c.updatedAt ?? ''}:${c.active}`).join('|')
}

/** How long until "Plays now" asks again for a change at changesAt: a second after it, at most a day. */
export function refreshIn(changesAt: string | undefined, now: number): number | null {
  if (!changesAt) return null
  const at = Date.parse(changesAt)
  if (Number.isNaN(at)) return null
  return Math.min(Math.max(at - now + 1000, 1000), 24 * 3600 * 1000)
}

/** A branch's zone as the server reads it (org.Branch.Location): UTC when it has none, or one not known. */
export function branchZone(timezone: string | undefined): string {
  if (!timezone) return 'UTC'
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone: timezone })
    return timezone
  } catch {
    return 'UTC'
  }
}

/** The chosen moment as plays now sends it: a branch wall time to the minute, or empty for now. */
export function momentParam(value: string): string {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) ? value.slice(0, 16) : ''
}

/** The heading over a chosen moment: the branch wall time said as written, not shifted to the browser's zone. */
export function momentHeading(at: string, lang: StaffLanguage): string {
  return t('signage.now.at', lang, { time: formatMoment(`${at}:00Z`, lang, { date: true, time: true, zone: 'UTC' }) })
}

/** A moment as a branch wall time, YYYY-MM-DDTHH:MM, as a datetime-local input holds it. */
function wallTime(at: Date, zone: string | undefined): string {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-GB', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    })
      .formatToParts(at)
      .map((p) => [p.type, p.value]),
  )
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`
}

/** The picker's bounds: a year either side of now, the earliest rounded up a minute so the server takes it. */
export function momentBounds(now: Date, zone: string | undefined): { min: string; max: string } {
  const min = new Date(now)
  min.setUTCFullYear(min.getUTCFullYear() - 1)
  const max = new Date(now)
  max.setUTCFullYear(max.getUTCFullYear() + 1)
  return { min: wallTime(new Date(min.getTime() + 60_000), zone), max: wallTime(max, zone) }
}

/** When what plays changes: the time alone for today, with the date for another day. */
export function changeMoment(at: string, now: Date, lang: StaffLanguage, zone: string | undefined): string {
  const today = formatMoment(at, lang, { date: true, zone }) === formatMoment(now, lang, { date: true, zone })
  return formatMoment(at, lang, today ? { time: true, zone } : { date: true, time: true, zone })
}
